import { useState, useEffect, useCallback, useRef } from "react";
import { mergePanelSignature } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { getManualRideById } from "../store/manualRides";
import {
  classes as classesCache,
  enqueue,
  getOutboxOp,
  horses as horsesCache,
  people as peopleCache,
  rides as ridesCache,
  scoreSheets,
  tests as testsCache,
} from "../local/db";
import { syncEngine } from "../local/sync";
import { useScoreContext } from "../context/ScoreContext";
import { normalizeRemarks } from "../util/remarks";

// Whole-sheet save is debounced so rapid mark entry collapses into one write.
const SAVE_DEBOUNCE_MS = 800;

// score-api owns these; never send them back on a save (the server recomputes
// totals on complete and stamps its own timestamps/id/org).
const SERVER_MANAGED = [
  "id",
  "orgId",
  "createdAt",
  "updatedAt",
  "totalPoints",
  "maxPoints",
  "percentage",
  // #1751 — panel signatures are written ONLY by POST /scoresheet/:id/
  // panel-signature (score-api lists `signatures` in SERVER_ONLY_FIELDS, so a
  // PUT could never set them anyway). signPanel() merges an OPTIMISTIC entry
  // into local state with `signatureHash: null` — only the server knows the
  // real S3 content address — and that entry would fail the sheet schema if a
  // debounced save carried it back up.
  "signatures",
];

// #1750 / equitation-class-sheet-design.md §11 — a judge-percentage sheet
// (the class-grid layout) has no movement math to derive a total from: the
// Total cell IS the score of record, typed directly by the scribe. Every
// other scoring model keeps percentage server-computed-only. Never widen
// this beyond the one model, and never drop the guard for anyone else — see
// scoreSheetIsClientTotal below for the single place that decides this.
const CLIENT_WRITABLE_TOTAL_SCORING_MODEL = "judge-percentage";

function scoreSheetIsClientTotal(test) {
  return test?.scoringModel === CLIENT_WRITABLE_TOTAL_SCORING_MODEL;
}

// #698 — a PUT rejected because the sheet is already confirmed/signed comes
// back from score-api as a 409 (server-enforced numeric freeze, #697).
// Translate that into copy a scribe can act on rather than a raw code. Any
// other failure (network blip, 5xx) gets a generic retry message — the
// outbox keeps retrying it regardless (see flush()'s retry loop).
// #1605 — takes the outbox op itself (httpStatus/errorClass/status), not just
// lastError text. lastError text-matching for "401"/"403"/"409" never
// actually worked in production: common-fe's installServerErrorInterceptor
// (installed for every call in scoreApi.js) rewrites err.message to the
// generic SERVER_ERROR_MESSAGE ("Server Error") before sync.js's catch block
// ever stores it, so none of those digits are in lastError to match against.
// httpStatus/errorClass are captured separately in sync.js's flush() for
// exactly this reason (see its own comment) and are what outboxDetail.js's
// isNotAssignedScribeError already keys on for the sync-detail dialog — this
// brings the per-sheet banner in line with that, instead of duplicating a
// second, broken text-matching scheme.
// Exported for direct unit testing (useScoreSheet.test.js) — the hook itself
// is not a good surface to drive this branch logic through.
export function describeSaveError(op) {
  const httpStatus = op?.httpStatus ?? null;
  const errorClass = op?.errorClass ?? null;
  if (httpStatus === 409) {
    return "This sheet's marks are already confirmed (or signed) and can no longer be changed. Comments still save normally.";
  }
  // #1289 — a 401 is a dead session, not a network problem. Saying
  // "once back online" here (while the header chip reads Online) is what sent
  // two people debugging connectivity for an hour on 2026-07-28. Name the
  // actual cause and the actual action.
  if (errorClass === "auth") {
    return "Your session expired. Sign in again to save — your work is safe on this device until you do.";
  }
  // #1562/#1605 — a 403 is NOT a dead session: the token verified fine and
  // score-api is refusing this scribe on purpose, almost always because
  // they're no longer the assigned scribe for this class/position. Sending
  // them to sign in again fixes nothing and costs them the sheet mid-ride
  // (this is the exact bug #1562 killed for the outbox chip/badge — this
  // banner was a second, separate surface still doing it). Name the real,
  // actionable fix instead.
  if (httpStatus === 403) {
    return "You're signed in, but not allowed to save this sheet — you may no longer be the assigned scribe for this class. Your work is safe on this device; ask your show manager to fix it.";
  }
  // Any other permanent (terminal) failure: sync.js's flush() marks it
  // "terminal" and never retries it again (see isSurfacedError/#1921) — don't
  // tell the scribe it's retrying when it categorically is not.
  if (op?.status === "terminal") {
    return "Save failed and won't retry on its own. Your work is safe on this device — escalate to your show manager.";
  }
  // Deliberately says nothing about being online: this branch covers network
  // blips AND 5xx AND anything unclassified, and the outbox retries all of
  // them regardless of connectivity.
  return "Save failed — it will keep retrying automatically.";
}

// #1605 — whether a just-flushed outbox op should populate the per-sheet
// saveError banner at all. Pulled out of the call site (like outboxDetail.js
// pulls isHeldForDisplay/isNotAssignedScribeError out of SyncStatus.jsx) so
// it's independently testable: #1921 routed every non-401 4xx (409, 403,
// validation) to outbox status "terminal" rather than "error", but this
// gate's pre-#1921 form checked "error" alone, which meant describeSaveError
// was never even called for a 403 (or a 409) — the banner just stayed empty.
export function isSaveErrorOp(status) {
  return status === "error" || status === "terminal";
}

// Map the local sheet to a create/update body: drop server-managed + undefined
// keys. The remaining shape matches score-common's scoreSheetSchema.
// `test` is the resolved Test for this sheet (may be null while still
// loading) — passed so percentage can be let through for the one scoring
// model that owns it client-side (see scoreSheetIsClientTotal above).
function toBody(sheet, test) {
  const clientTotal = scoreSheetIsClientTotal(test);
  const body = {};
  for (const [k, v] of Object.entries(sheet)) {
    if (k === "percentage" && clientTotal) {
      if (v !== undefined) body[k] = v;
      continue;
    }
    if (SERVER_MANAGED.includes(k)) continue;
    if (v !== undefined) body[k] = v;
  }
  return body;
}

/**
 * Loads the ScoreSheet for a (rideId, judgeId) pair from score-api — hydrating
 * ride/class/test/horse/rider — and returns it plus mutators. Writes are the
 * card #364 "simplified offline sync": every edit debounces a whole-sheet save
 * to the API; if the save fails (offline), the entire sheet is parked in
 * IndexedDB (last-write-wins) and replayed on reconnect, so nothing is lost.
 *
 * judgeId is the scribe's judge position (C/E/H/M/B) for the class — one judge
 * per position, so the position is a stable per-ride grouping key. (FVD gives
 * judge names, not Person ids; resolving real judge ids is deferred.)
 *
 * #1888 investigation finding: `loading` only flips back to true for a
 * genuine new sheet (a real ride/judge switch, or first mount) — NOT for a
 * same-sheet revalidation, which includes every re-run this hook's load
 * effect does when `online` flips (a reconnect drain, or a real venue-wifi
 * blip). ScoringForm gates its whole tree behind `if (loading ||
 * resolvingPosition) return <spinner>` — TWO inputs, not one. This hook only
 * owns `loading`; `resolvingPosition` comes from useAssignedPosition
 * (apps/scribe-fe/src/hooks/useAssignedPosition.js), which had the SAME bug
 * (an unconditional `loading: true` on every `online`-triggered re-run) and
 * is fixed the same way (a same-key revalidation guard). Any caller that
 * lets EITHER hook's loading-shaped value go back to a "not ready yet" state
 * on a mere revalidation will unmount/remount the scoring form again — same
 * class of bug as #153/#154 (scroll reset, focus loss, visible flash). A
 * future per-tick value (e.g. #1889's venue clock) must live OUTSIDE both
 * hooks' state/effects entirely, not as a new dependency of either load
 * effect or a new field that forces either hook's loading value truthy.
 *
 * #1781 — `preloaded` (optional) lets a caller that already has the ride,
 * class and/or test hand them in so this hook skips fetching them itself.
 * ClassGrid mounts one useScoreSheet per row (one per rider), and its own
 * useRidesForClass/useClassGridTest have already fetched the class and every
 * ride in it plus resolved the test — without this, each row redundantly
 * repeated all three fetches, turning a 25-rider class into ~150 requests on
 * mount over venue wifi. Any param left unset (every other caller, e.g.
 * ScoringForm) falls through to this hook's own fetch exactly as before —
 * getHorse/getPerson/listScoreSheets are NOT covered here (deferred, see the
 * #1781 card comment) since they are per-ride identity/sheet data the caller
 * does not have grouped by ride.
 */
export function useScoreSheet(rideId, judgeId, preloaded = {}) {
  const preloadedRide = preloaded?.ride ?? null;
  const preloadedKlass = preloaded?.klass ?? null;
  const preloadedTest = preloaded?.test ?? null;
  const api = useScoreApi();
  const { online } = useScoreContext();
  const [state, setState] = useState({
    loading: true,
    error: null,
    ride: null,
    klass: null,
    test: null,
    horse: null,
    rider: null,
    judgeName: null,
    sheet: null,
    isCompleted: false,
    isConfirmed: false,
    // #901 — true once this sheet has EVER been signed (server-stamped
    // everSignedAt, which reopenScoreSheet does not clear). Drives the
    // reopened-sheet "Save" footer in ScoringForm.
    wasEverSigned: false,
    saveError: null,
  });

  // Refs so the debounced save and drain always see the latest sheet + the
  // server id without re-subscribing.
  const serverIdRef = useRef(null);
  const latestSheet = useRef(null);
  // Mirrors state.test for the debounced persist() closure below (which
  // can't depend on React state without re-subscribing) — see toBody's
  // scoringModel-conditional percentage handling.
  const testRef = useRef(null);
  // #266 CI flake — the `${rideId}::${judgeId}` this load effect last populated
  // latestSheet.current for. Lets a same-sheet re-run of the effect (deps flap:
  // an unstable `api`/`online`, or any re-render) tell itself apart from a real
  // ride switch, so it keeps the scribe's in-session edits instead of clobbering
  // them from a possibly-stale cache (see the load effect's #906 keepSheet note).
  const loadedKey = useRef(null);
  const saveTimer = useRef(null);
  // Serializes saves so a debounced create can't fire twice before the first
  // returns the server id (which would create duplicate sheets).
  const inflight = useRef(Promise.resolve());

  useEffect(() => {
    let cancelled = false;
    // #627 — judgeId (the scribe's seat) is derived from the ride's class, so it
    // arrives a tick after the ride id. A sheet is keyed by (ride, judge): until
    // the seat is known there is nothing to load and nothing safe to save, so
    // stay in the loading state rather than assembling a judge-less sheet.
    if (!judgeId) {
      setState((s) => ({ ...s, loading: true, error: null }));
      return undefined;
    }

    // #266 CI flake — is this a re-run for the SAME sheet (deps flapped) or a
    // genuine ride switch? On a same-sheet re-run the scribe's local edits in
    // latestSheet.current are authoritative (#906: the scribe is the sole
    // writer once loaded), so BOTH the cache and the api applies below must
    // keep them — not just the api one. Deriving `cleared` in
    // applySheetWithReason isn't enough: apply(cacheCtx) with keepSheet=false
    // re-seats latestSheet.current from a cache that may still hold a
    // just-removed deduction/error (flush updated the server, not the cached
    // sheet doc), silently resurrecting it. On a real ride switch the key
    // differs, so we fall through to taking the fresh cache/api sheet.
    const sheetKey = `${rideId}::${judgeId}`;
    const keepLocal = loadedKey.current === sheetKey && latestSheet.current != null;
    loadedKey.current = sheetKey;

    // #1888 — `online` is a dep of this effect (see the array below) so a
    // reconnect drain re-runs it; that used to ALWAYS flip loading back to
    // true first. ScoringForm has a hard `if (loading) return <spinner>`
    // gate that unmounts the entire scoring form for that window, which
    // resets scroll position and drops form focus (bug #153) and, since the
    // toggle can flap during a real connectivity blip, visibly flashes (bug
    // #154) — see docs comment on the useScoreSheet export / #1888 card
    // investigation. `keepLocal` already distinguishes "same sheet the
    // scribe is looking at, just revalidating" from a genuine new load (a
    // real ride/judge switch, or first mount): only the latter should show
    // the full-screen loading gate. Skip resetting serverIdRef too — on a
    // same-sheet revalidation it's still correct and apply() below refreshes
    // it from whatever the reload finds anyway.
    if (!keepLocal) {
      setState((s) => ({ ...s, loading: true, error: null }));
      serverIdRef.current = null;
    }

    // Build the display context from a set of fetched entities. The sheet base
    // prefers the queued (unsynced) local edits — the outbox op body (#422) —
    // over the server copy, so it's identical whether the parts came from the
    // cache or the network.
    const assemble = async ({ ride, klass, test, apiHorse, apiRider, serverSheet, sheetRideId }) => {
      // Manual rides carry free-text names (no horse/rider ids) — synthesize
      // lightweight objects so ScoringForm's chips render unchanged.
      const horse =
        apiHorse ??
        (ride.isManual && ride.horseName && ride.horseName !== "—"
          ? { id: null, name: ride.horseName }
          : null);
      const rider =
        apiRider ??
        (ride.isManual && ride.riderName && ride.riderName !== "—"
          ? { id: null, firstName: ride.riderName, lastName: "" }
          : null);

      const pendingOp = await getOutboxOp(
        `scoreSheet:${sheetRideId}::${judgeId}`,
      ).catch(() => null);
      const base =
        pendingOp?.payload?.body ??
        serverSheet ?? {
          rideId: sheetRideId,
          judgeId,
          testId: test?.id,
          movementScores: [],
          collectives: {},
          generalRemarks: "",
        };
      const sheet = structuredClone(base);
      sheet.rideId = sheetRideId;
      sheet.judgeId = judgeId;
      if (!sheet.testId) sheet.testId = test?.id;

      // #669 — normalize on read: a sheet saved before "no newlines" was a
      // rule (or written by an older client) may already have a line break
      // in a remarks value. Fix it up here, at the earliest point the sheet
      // enters the app, rather than only guarding new writes.
      if (sheet.generalRemarks) {
        sheet.generalRemarks = normalizeRemarks(sheet.generalRemarks);
      }
      if (sheet.movementScores?.length) {
        sheet.movementScores = sheet.movementScores.map((m) =>
          m.remarks ? { ...m, remarks: normalizeRemarks(m.remarks) } : m,
        );
      }
      if (sheet.collectives) {
        for (const key of Object.keys(sheet.collectives)) {
          const c = sheet.collectives[key];
          if (c?.remarks) {
            sheet.collectives[key] = { ...c, remarks: normalizeRemarks(c.remarks) };
          }
        }
      }

      const judgeName = judgeId ? klass?.judgeAssignments?.[judgeId]?.[0] ?? null : null;
      return {
        ride,
        klass,
        test,
        horse,
        rider,
        judgeName,
        sheet,
        serverSheetId: serverSheet?.id ?? null,
        isCompleted: Boolean(serverSheet?.completedAt),
        // #698 — confirmed once marksConfirmedAt is stamped (server-side,
        // one-way). A completed (signed) sheet was necessarily confirmed
        // first, so treat completedAt as confirmed too (back-compat for
        // sheets signed before #696/#697).
        isConfirmed: Boolean(serverSheet?.marksConfirmedAt || serverSheet?.completedAt),
        // #901 — durable across reopen; see useState init above.
        wasEverSigned: Boolean(serverSheet?.everSignedAt),
        hasPending: Boolean(pendingOp),
      };
    };

    // Apply a context to state. Never downgrade a known server id to null (the
    // create may not have reached the server yet), and keep local edits on a
    // background revalidation (keepSheet) so typing isn't clobbered.
    const apply = (ctx, keepSheet = false) => {
      serverIdRef.current = ctx.serverSheetId ?? serverIdRef.current;
      testRef.current = ctx.test ?? testRef.current;
      if (!keepSheet) latestSheet.current = ctx.sheet;
      setState((s) => ({
        loading: false,
        error: null,
        ride: ctx.ride,
        klass: ctx.klass,
        test: ctx.test,
        horse: ctx.horse,
        rider: ctx.rider,
        judgeName: ctx.judgeName,
        sheet: keepSheet ? latestSheet.current ?? ctx.sheet : ctx.sheet,
        isCompleted: ctx.isCompleted || s.isCompleted,
        // Never downgrade — once confirmed, stays confirmed even if a
        // background revalidation races ahead of a still-queued op.
        isConfirmed: ctx.isConfirmed || s.isConfirmed,
        // #901 — same never-downgrade pattern; server never clears this
        // anyway, but keep it consistent with isCompleted/isConfirmed.
        wasEverSigned: ctx.wasEverSigned || s.wasEverSigned,
        saveError: s.saveError,
      }));
    };

    // #203 / #380 — an emergency manual ride isn't on the day sheet, so it's
    // read from the local mirror instead of GET /ride.
    const fromCache = async () => {
      const manual = getManualRideById(rideId);
      const ride = manual ?? preloadedRide ?? (await ridesCache.get(rideId)) ?? null;
      if (!ride) return null;
      const sheetRideId = manual ? manual.serverId ?? rideId : rideId;
      const klass =
        preloadedKlass ??
        (ride.classId ? (await classesCache.get(ride.classId)) ?? null : null);
      // #462 — ride.testId is the ONLY source. Never fall back to klass.testId:
      // in a Test-of-Choice class the class test is meaningless, so substituting
      // it would silently score a ride against the wrong test. A missing testId
      // must fail loud (see ScoringForm's "no test assigned" branch), not score.
      const testId = ride.testId;
      const test = preloadedTest ?? (testId ? (await testsCache.get(testId)) ?? null : null);
      const apiHorse = ride.horseId ? (await horsesCache.get(ride.horseId)) ?? null : null;
      const apiRider = ride.riderId ? (await peopleCache.get(ride.riderId)) ?? null : null;
      const sheets = judgeId ? await scoreSheets.byRideJudge(sheetRideId, judgeId) : [];
      const serverSheet = sheets.find((s) => s.judgeId === judgeId) ?? null;
      return assemble({ ride, klass, test, apiHorse, apiRider, serverSheet, sheetRideId });
    };

    const fromApi = async () => {
      const manual = getManualRideById(rideId);
      const ride = manual ?? preloadedRide ?? unwrap(await api.rides.getRide(rideId));
      const sheetRideId = manual ? manual.serverId ?? rideId : rideId;
      const klass =
        preloadedKlass ??
        (ride.classId
          ? await api.classes.getClass(ride.classId).then(unwrap).catch(() => null)
          : null);
      // #462 — ride.testId only (see fromCache); no class-test fallback.
      const testId = ride.testId;
      const [test, apiHorse, apiRider] = await Promise.all([
        preloadedTest
          ? Promise.resolve(preloadedTest)
          : testId
          ? api.tests.getTest(testId).then(unwrap).catch(() => null)
          : null,
        ride.horseId
          ? api.horses.getHorse(ride.horseId).then(unwrap).catch(() => null)
          : null,
        ride.riderId
          ? api.people.getPerson(ride.riderId).then(unwrap).catch(() => null)
          : null,
      ]);
      // score-api's listScoreSheets queries the rideId GSI and does NOT also
      // filter by judgeId, so it returns every judge's sheet — pick this one's.
      const sheets = judgeId
        ? await api.scoreSheets
            .listScoreSheets(sheetRideId, judgeId)
            .then(unwrap)
            .catch(() => [])
        : [];
      const serverSheet = sheets.find((s) => s.judgeId === judgeId) ?? null;

      // Write-through to the cache so this ride opens offline next time. Skip
      // when the caller preloaded the entity (#1781): it already came from
      // this caller's own cache-writing fetch (ClassGrid's useRidesForClass /
      // useClassGridTest), and a preloaded `ride` here may be denormalized
      // (status/percentage merged in by useRidesForClass) — writing that back
      // would pollute the raw ride cache other readers rely on.
      if (!ride.isManual && !preloadedRide) await ridesCache.put(ride);
      if (klass && !preloadedKlass) await classesCache.put(klass);
      if (test && !preloadedTest) await testsCache.put(test);
      if (apiHorse) await horsesCache.put(apiHorse);
      if (apiRider) await peopleCache.put(apiRider);
      if (sheets.length) await scoreSheets.putAll(sheets);

      return assemble({ ride, klass, test, apiHorse, apiRider, serverSheet, sheetRideId });
    };

    (async () => {
      let cacheCtx = null;
      try {
        cacheCtx = await fromCache();
      } catch {
        cacheCtx = null;
      }
      if (!cancelled && cacheCtx) apply(cacheCtx, keepLocal);

      if (online) {
        try {
          const apiCtx = await fromApi();
          // #906 — never-downgrade, extended to sheet CONTENT: once a sheet has
          // been shown locally (cacheCtx, however it got there), a background
          // revalidation must never overwrite marks/collectives/annotations
          // with whatever fromApi() just fetched. This used to be gated on
          // `apiCtx.hasPending` (an outbox op still queued) alone, on the
          // assumption that once a save flushed there was nothing left to
          // protect — but a flush racing a slower in-flight GET (or a GET
          // response reflecting a pre-edit snapshot) can land AFTER the outbox
          // has already drained, clobbering just-typed marks with stale
          // server data (#906). The scribe is the sheet's only writer once
          // loaded, so keeping latestSheet.current here is always safe; only
          // a truly cold load (no cacheCtx at all) takes fromApi()'s sheet.
          if (!cancelled) apply(apiCtx, keepLocal || Boolean(cacheCtx));
        } catch (err) {
          if (!cancelled && !cacheCtx) {
            setState({
              loading: false,
              error: err,
              ride: null,
              klass: null,
              test: null,
              horse: null,
              rider: null,
              judgeName: null,
              sheet: null,
              isCompleted: false,
            });
          }
        }
      } else if (!cancelled && !cacheCtx) {
        // Offline with nothing cached — settle out of the loading state.
        setState((s) => ({ ...s, loading: false }));
      }
    })();

    return () => {
      cancelled = true;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [api, rideId, judgeId, online]);

  // Persist the whole sheet through the local-first outbox (#422): every save
  // enqueues the entire sheet (upsert per ride+judge, last-write-wins) and,
  // when online, flushes immediately (write-through) so scores reach the cloud
  // in near-real-time. Nothing is lost regardless of connectivity — offline the
  // op simply waits in the outbox for the reconnect drain.
  const persist = useCallback(
    (sheet) => {
      // No seat, no sheet key (#627) — refuse rather than write a judge-less sheet.
      if (!sheet || !judgeId) return inflight.current;
      const run = async () => {
        // A manual ride's server id may have been stamped since the last save;
        // until it is, the op carries the temp id so the engine remaps it once
        // the ride syncs (rather than POSTing a dangling ride reference).
        const manual = getManualRideById(rideId);
        const effRideId = manual ? manual.serverId ?? rideId : rideId;
        const isTempRide = Boolean(manual && !manual.serverId);
        const entityKey = `${effRideId}::${judgeId}`;
        const body = { ...toBody(sheet, testRef.current), rideId: effRideId };

        // Resolve the server sheet id: from load, or the id the engine cached
        // once a create drained.
        if (!serverIdRef.current) {
          const cached = await scoreSheets
            .byRideJudge(effRideId, judgeId)
            .catch(() => []);
          if (cached[0]?.id) serverIdRef.current = cached[0].id;
        }
        const serverId = serverIdRef.current;

        await enqueue({
          opId: `scoreSheet:${entityKey}`,
          type: serverId ? "updateScoreSheet" : "createScoreSheet",
          entityKey,
          tempId: isTempRide ? effRideId : null,
          payload: { rideId: effRideId, judgeId, serverId: serverId ?? null, body },
        });

        // #515 — the enqueue above is the local commit: the write is already
        // durable in the outbox, which is what a caller's button spinner
        // should key off (a PWA optimistic save "completes" the moment it's
        // queued, same contract completeScoreSheet already documents for the
        // fully-offline case below). flush() is a network round-trip that
        // can be slow or hang on flaky venue wifi even while
        // `navigator.onLine` says true — awaiting it here would hold the
        // Save/Confirm/Sign button's spinner hostage to that latency. Fire it
        // in the background instead; reconcile serverIdRef and surface a
        // post-flush error once it lands, but don't block on it.
        if (syncEngine.isOnline()) {
          syncEngine
            .flush()
            .then(async () => {
              const after = await scoreSheets
                .byRideJudge(effRideId, judgeId)
                .catch(() => []);
              if (after[0]?.id) serverIdRef.current = after[0].id;
              // #698 — flush() swallows a per-op failure internally (it marks
              // the outbox entry "error" or "terminal" and keeps draining
              // everything else); surface it here so a 409 (stale tab that
              // already confirmed/signed) reaches the UI instead of failing
              // silently.
              // #1605 — "terminal" included alongside "error": #1921 routed
              // every non-401 4xx (409, 403, validation) to status
              // "terminal", not "error". Gating on "error" alone (the
              // pre-#1921 check) silently dropped this banner for all of
              // them, including the 409/403 cases describeSaveError already
              // had dedicated copy for.
              const outboxOp = await getOutboxOp(
                `scoreSheet:${entityKey}`,
              ).catch(() => null);
              setState((s) => ({
                ...s,
                saveError: isSaveErrorOp(outboxOp?.status)
                  ? describeSaveError(outboxOp)
                  : null,
              }));
            })
            .catch(() => {});
        }
        // Local commit is done either way: refresh the pending-sync badge
        // from the outbox now rather than waiting on the background flush.
        await syncEngine.refreshPending();
      };
      // Chain onto the previous save so enqueues never overlap.
      inflight.current = inflight.current.then(run, run);
      return inflight.current;
    },
    [rideId, judgeId]
  );

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(latestSheet.current), SAVE_DEBOUNCE_MS);
  }, [persist]);

  // Single mutation path: derive the next sheet from the ref, update state, and
  // schedule a save. Using the ref (not state) as the source keeps the save
  // debounce correct across rapid edits.
  const applySheet = useCallback(
    (updater) => {
      const cur = latestSheet.current;
      if (!cur) return;
      const next = updater(cur);
      latestSheet.current = next;
      setState((s) => (s.sheet ? { ...s, sheet: next } : s));
      scheduleSave();
    },
    [scheduleSave]
  );

  const setMovementScore = useCallback(
    (movementNumber, fields) => {
      applySheet((sheet) => {
        const existing = sheet.movementScores ?? [];
        const idx = existing.findIndex((m) => m.movementNumber === movementNumber);
        const next =
          idx >= 0
            ? existing.map((m, i) => (i === idx ? { ...m, ...fields, movementNumber } : m))
            : [...existing, { movementNumber, ...fields }];
        return { ...sheet, movementScores: next };
      });
    },
    [applySheet]
  );

  const setCollective = useCallback(
    (key, fields) => {
      applySheet((sheet) => {
        const prev = sheet.collectives?.[key] ?? {};
        return {
          ...sheet,
          collectives: { ...(sheet.collectives ?? {}), [key]: { ...prev, ...fields } },
        };
      });
    },
    [applySheet]
  );

  // #208 / #209 — merge arbitrary top-level sheet fields (eliminations,
  // errors, annotated prose, judgeSignature, …).
  const setSheetFields = useCallback(
    (fields) => applySheet((sheet) => ({ ...sheet, ...fields })),
    [applySheet]
  );

  // #266 — removing a point deduction or clearing a (non-Blood) elimination
  // requires a scribe-entered reason, forensically recorded. `changeReason`
  // rides the existing #571 audit pipeline: score-api reads it straight off
  // the PUT body (bypassing the schema, so no client regen) and stamps it on
  // the sheet item; the audit-stream consumer reads it into the WORM audit
  // trail (see docs/score/audit-design.md). It must NOT linger in local sheet
  // state past this one save — every subsequent debounced save resends the
  // WHOLE sheet, and a lingering reason would misattribute unrelated later
  // edits — so this bypasses the debounce (like complete()/confirmMarks())
  // and strips the reason back out of local state right after the send.
  const applySheetWithReason = useCallback(
    async (updater, reason) => {
      const cur = latestSheet.current;
      if (!cur) return;
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      const withReason = { ...updater(cur), changeReason: reason };
      latestSheet.current = withReason;
      setState((s) => (s.sheet ? { ...s, sheet: withReason } : s));
      await persist(withReason);
      // #266 CI flake — build `cleared` from the exact object we just sent,
      // not from whatever latestSheet.current holds by now: a concurrent
      // write landing during the persist await (e.g. a prior confirmMarks()
      // call's #698 tail, which re-merges server state after its own flush)
      // can overwrite latestSheet.current with stale data that still
      // carries the just-removed content, and deriving `cleared` from THAT
      // silently resurrects it. If nothing else touched latestSheet.current
      // mid-flight, strip changeReason off withReason directly — the
      // removal survives no matter how slow persist was. If something did
      // race in, don't stomp its fields — just strip changeReason off
      // whatever is there now.
      const { changeReason: _omit, ...clearedFromSent } = withReason;
      const cleared =
        latestSheet.current === withReason
          ? clearedFromSent
          : { ...latestSheet.current, changeReason: undefined };
      latestSheet.current = cleared;
      setState((s) => (s.sheet ? { ...s, sheet: cleared } : s));
    },
    [persist]
  );

  const setGeneralRemarks = useCallback(
    (value) => applySheet((sheet) => ({ ...sheet, generalRemarks: value })),
    [applySheet]
  );

  // R1-UI-024 — captured arena-entry time in seconds (set on bell-stop).
  const setEntryTimeSeconds = useCallback(
    (seconds) => applySheet((sheet) => ({ ...sheet, entryTimeSeconds: seconds })),
    [applySheet]
  );

  // Sign + complete: flush the pending save (the sheet already carries the
  // judgeSignature via setSheetFields), then enqueue a completeScoreSheet op so
  // the server computes totals + stamps completedAt. Works offline — the op
  // resolves its server id from the cache once the create drains (the engine
  // remaps a temp ride's op after the ride syncs), so completion round-trips on
  // reconnect. Mark complete optimistically: the scribe has signed.
  const complete = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    await persist(latestSheet.current);

    const manual = getManualRideById(rideId);
    const effRideId = manual ? manual.serverId ?? rideId : rideId;
    const entityKey = `${effRideId}::${judgeId}`;
    await enqueue({
      opId: `complete:${entityKey}`,
      type: "completeScoreSheet",
      entityKey,
      tempId: manual && !manual.serverId ? effRideId : null,
      payload: { serverId: serverIdRef.current ?? null, rideId: effRideId, judgeId },
    });

    latestSheet.current = {
      ...latestSheet.current,
      completedAt: latestSheet.current?.completedAt ?? new Date().toISOString(),
    };
    // #901 — wasEverSigned is monotonic/durable server-side too; setting it
    // here optimistically keeps it consistent with isCompleted.
    setState((s) => ({ ...s, isCompleted: true, wasEverSigned: true, sheet: latestSheet.current }));
    // #515 — isCompleted is already flipped optimistically above (the local
    // commit); don't hold the Sign/Save button's spinner on the network
    // flush that follows. Fire it in the background like persist() does.
    if (syncEngine.isOnline()) syncEngine.flush().catch(() => {});
    await syncEngine.refreshPending();
  }, [persist, rideId, judgeId]);

  // #698 — Confirm Score: the first of the two scoring stages. Flushes the
  // pending save (so the server confirms against the scribe's latest marks),
  // then enqueues a confirmScoreSheetMarks op through the SAME offline outbox
  // as every other mutation (createScoreSheet/updateScoreSheet/complete) —
  // no separate offline mechanism. One-way: there is no matching "unconfirm"
  // op anywhere in this file or the sync engine, by design (owner ruling).
  // Marked optimistically (isConfirmed true, marksConfirmedAt stamped) so the
  // FE freezes numeric entry immediately even offline; once online the
  // confirm-marks response (totalPoints/maxPoints/percentage) is pulled back
  // out of the cache the sync engine just wrote, so the real server totals
  // replace the optimistic guess.
  const confirmMarks = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    await persist(latestSheet.current);

    const manual = getManualRideById(rideId);
    const effRideId = manual ? manual.serverId ?? rideId : rideId;
    const entityKey = `${effRideId}::${judgeId}`;
    await enqueue({
      opId: `confirmMarks:${entityKey}`,
      type: "confirmScoreSheetMarks",
      entityKey,
      tempId: manual && !manual.serverId ? effRideId : null,
      payload: { serverId: serverIdRef.current ?? null, rideId: effRideId, judgeId },
    });

    latestSheet.current = {
      ...latestSheet.current,
      marksConfirmedAt: latestSheet.current?.marksConfirmedAt ?? new Date().toISOString(),
    };
    setState((s) => ({ ...s, isConfirmed: true, sheet: latestSheet.current }));

    // #515 — isConfirmed is already flipped optimistically above; the real
    // server totals are a nice-to-have refinement, not something the Confirm
    // button's spinner should block on. Pull them back in the background
    // once flush lands instead of awaiting the network round-trip here.
    if (syncEngine.isOnline()) {
      syncEngine
        .flush()
        .then(async () => {
          const after = await scoreSheets
            .byRideJudge(effRideId, judgeId)
            .catch(() => []);
          const server = after.find((s) => s.judgeId === judgeId);
          if (server) {
            latestSheet.current = { ...latestSheet.current, ...server };
            setState((s) => ({ ...s, sheet: latestSheet.current }));
          }
        })
        .catch(() => {});
    }
    await syncEngine.refreshPending();
  }, [persist, rideId, judgeId]);

  // #1751 — one panel judge signs THIS row's sheet, as part of the class-level
  // signing ceremony (docs/score/equitation-class-sheet-design.md §6). Unlike
  // complete(), this is NOT a finalization: a DSM sheet takes three of these,
  // one per seated judge, and only then will completeScoreSheet stop refusing
  // (#1748's panel gate).
  //
  // Flushes the pending save first, for the same reason confirmMarks() does:
  // the server refuses to sign a sheet whose marks are not confirmed, and the
  // confirm that precedes this needs the scribe's latest cells to have landed.
  //
  // The optimistic local merge uses score-common's mergePanelSignature — the
  // same function the server applies — so an offline signature shows up in the
  // grid immediately and in exactly the shape the drained response will
  // replace it with. `signatureHash` is deliberately null in the optimistic
  // entry: the real hash is the S3 content address of the judge's stored
  // signature, which only the server can know. Nothing in scribe renders the
  // hash; the grid only asks WHICH judges have signed.
  const signPanel = useCallback(
    async (panelJudgeId) => {
      if (!panelJudgeId) return;
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      await persist(latestSheet.current);

      const manual = getManualRideById(rideId);
      const effRideId = manual ? manual.serverId ?? rideId : rideId;
      const entityKey = `${effRideId}::${judgeId}`;
      await enqueue({
        // Per JUDGE as well as per sheet: two judges signing the same row are
        // two separate ops, and a re-sign by the same judge replaces its own
        // queued op rather than stacking a second one.
        opId: `panelSign:${entityKey}:${panelJudgeId}`,
        type: "panelSignScoreSheet",
        entityKey,
        tempId: manual && !manual.serverId ? effRideId : null,
        payload: {
          serverId: serverIdRef.current ?? null,
          rideId: effRideId,
          judgeId,
          panelJudgeId,
        },
      });

      latestSheet.current = {
        ...latestSheet.current,
        signatures: mergePanelSignature(latestSheet.current?.signatures, {
          judgeId: panelJudgeId,
          signatureHash: null,
          signedAt: new Date().toISOString(),
        }),
      };
      setState((s) => (s.sheet ? { ...s, sheet: latestSheet.current } : s));

      if (syncEngine.isOnline()) syncEngine.flush().catch(() => {});
      await syncEngine.refreshPending();
    },
    [persist, rideId, judgeId]
  );

  return {
    ...state,
    setMovementScore,
    setCollective,
    setSheetFields,
    setGeneralRemarks,
    setEntryTimeSeconds,
    applySheetWithReason,
    complete,
    confirmMarks,
    signPanel,
  };
}
